import React, { useState, useRef, useEffect } from "react";
import { Video, Play, Pause, RotateCcw } from "lucide-react";
import axios from "../../../routers/axios";

// ✅ Thêm đầy đủ CSS của TinyMCE content để hiển thị giống CMS
import "tinymce/skins/content/default/content.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/skins/ui/oxide/skin.min.css";

// ✅ Hàm decode HTML entities
const decodeHTML = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const AITeacher = ({
  type = "instruction",
  scenarioId = null,
  testId = "test1",
  taskId = null,
}) => {
  const [scenarioData, setScenarioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // video state
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  // =====================================================
  // 🚀 FETCH API READING / WRITING
  // =====================================================
  useEffect(() => {
    if (!scenarioId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const endpoint =
          type === "writing"
            ? `/scenarios/writing/${scenarioId}`
            : `/scenarios/reading/${scenarioId}`;
        const res = await axios.get(endpoint);
        console.log("✅ Scenario API response:", res.data);

        // ====================== 📘 READING ======================
        if (type === "reading") {
          setScenarioData({
            instruction_reading: {
              title: decodeHTML(res.data.reading_title || ""),
              content: decodeHTML(
                res.data.reading_instruction ||
                res.data.reading_description ||
                res.data.passage_content ||
                "No instruction available"
              ),
            },
            videoFile: res.data.video_filename || res.data.reading_video || null,
            scenario_name: decodeHTML(
              res.data.reading_title || "Reading Scenario"
            ),
          });
        }

        // ====================== ✍️ WRITING ======================
        else {
          // ✅ Thêm khối messageSection (đã chỉnh theo yêu cầu)
          const messageSection = `
  ${res.data.message_image
              ? `
      <div class="flex justify-center my-6">
        <div class="bg-gray-50 rounded-xl border border-gray-200 p-3 shadow-sm flex justify-center">
          <img 
            src="${res.data.message_image.startsWith("http")
                ? res.data.message_image
                : `/AI_Video/${res.data.message_image}`
              }"
            alt="Message"
            class="max-w-[420px] w-full rounded-lg object-contain"
          />
        </div>
      </div>
    `
              : ""
            }
`;
          // ✅ Ghép đầy đủ nội dung writing
          const combinedContent = `
            ${res.data.instruction_intro ? `<p>${res.data.instruction_intro}</p>` : ""}
            ${res.data.instruction_additional ? `<p>${res.data.instruction_additional}</p>` : ""}
            ${messageSection}
          `;

          // ✅ Xử lý video hoặc ảnh minh họa
          const fileName =
            res.data.instruction_video || res.data.instruction_image || null;
          let videoFile = null;
          let imageFile = null;

          if (fileName) {
            const ext = fileName.split(".").pop().toLowerCase();
            if (["mp4", "mov", "webm"].includes(ext)) {
              videoFile = fileName;
            } else if (["jpg", "jpeg", "png", "gif"].includes(ext)) {
              imageFile = fileName;
            }
          }

          setScenarioData({
            instruction_writing: {
              title: decodeHTML(res.data.writing_title || ""),
              duration: `${res.data.estimated_time_minutes || 30} mins`,
              content: decodeHTML(
                combinedContent || "No writing instruction available"
              ),
            },
            videoFile,
            imageFile,
            scenario_name: decodeHTML(
              res.data.writing_title || "Writing Scenario"
            ),
          });
        }
      } catch (err) {
        console.error("❌ Error fetching scenario data:", err);
        setError(err.message || "Failed to load scenario data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [scenarioId, type]);

  // =====================================================
  // 🚀 VIDEO CONTROLS
  // =====================================================
  const handleVideoLoad = () => setIsVideoLoaded(true);
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };
  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // =====================================================
  // 🚀 UI STATES
  // =====================================================
  if (loading)
    return (
      <div className="bg-white p-6 rounded-xl shadow text-center text-gray-600">
        Loading AI Teacher...
      </div>
    );

  if (error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-center">
        ⚠️ {error}
      </div>
    );

  if (!scenarioData)
    return (
      <div className="bg-white p-6 rounded-xl shadow text-center text-gray-500">
        No scenario data found.
      </div>
    );

  const videoFile = scenarioData.videoFile;
  const title =
    type === "writing"
      ? scenarioData.instruction_writing?.title
      : scenarioData.instruction_reading?.title;
  const duration =
    type === "writing"
      ? scenarioData.instruction_writing?.duration
      : scenarioData.instruction_reading?.duration;
  const content =
    type === "writing"
      ? scenarioData.instruction_writing?.content
      : scenarioData.instruction_reading?.content;

  // =====================================================
  // 🚀 RENDER
  // =====================================================
  return (
    <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-blue-200 shadow-lg mt-6">
      <div className="flex items-start space-x-2">
        {/* LEFT - Video */}
        <div className="flex justify-center items-start w-[220px]">
          {scenarioData.videoFile ? (
            <div className="relative bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 p-1">
              <video
                key={scenarioData.videoFile}
                ref={videoRef}
                className="rounded-lg object-contain bg-black"
                style={{
                  width: "190px",
                  height: "280px",
                  borderRadius: "0.75rem",
                  backgroundColor: "#000",
                }}
                controls
                preload="metadata"
                onLoadedData={handleVideoLoad}
              >
                <source
                  src={
                    scenarioData.videoFile.startsWith("http")
                      ? scenarioData.videoFile
                      : `/AI_Video/${scenarioData.videoFile}`
                  }
                  type="video/mp4"
                />
              </video>
            </div>
          ) : scenarioData.imageFile ? (
            <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 p-1">
              <img
                src={
                  scenarioData.imageFile.startsWith("http")
                    ? scenarioData.imageFile
                    : `/AI_Video/${scenarioData.imageFile}`
                }
                alt="Writing Illustration"
                className="object-contain w-[190px] h-[280px] rounded-lg"
              />
            </div>
          ) : (
            <div className="bg-gray-100 rounded-xl p-6 text-center w-[200px] h-[320px] flex flex-col items-center justify-center border border-gray-200">
              <Video className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-gray-500">No media available</p>
            </div>
          )}
        </div>

        {/* RIGHT - Info */}
        <div className="w-3/4">
          <div className="bg-white bg-opacity-70 rounded-xl p-6 shadow-sm border border-blue-100 h-full">
            {(title || duration) && (
              <div className="mb-4">
                {title && (
                  <div
                    className="text-lg font-bold text-gray-800 mb-1"
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                )}
                {duration && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                      ⏱ {duration}
                    </span>
                  </div>
                )}
              </div>
            )}
            {content ? (
              <div className="text-gray-700 text-sm leading-relaxed">
                <div
                  className="mce-content-body"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            ) : (
              <p className="text-gray-500 italic">
                No instruction content available.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITeacher;
